import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtIST } from "@/lib/datetime";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
    CartesianGrid, LineChart, Line, Cell,
} from "recharts";

export default function Analytics() {
    const [peak, setPeak] = useState([]);
    const [trend, setTrend] = useState([]);
    const [frequent, setFrequent] = useState([]);

    useEffect(() => {
        (async () => {
            const [p, t, f] = await Promise.all([
                api.get("/gate-check/analytics/peak-hours?days=30"),
                api.get("/gate-check/analytics/daily-trend?days=14"),
                api.get("/gate-check/analytics/frequent-visitors?limit=10"),
            ]);
            setPeak(p.data); setTrend(t.data); setFrequent(f.data);
        })();
    }, []);

    return (
        <div>
            <PageHeader
                overline="Insights"
                title="Analytics"
                subtitle="Peak visit hours, daily trends and frequent visitor identification."
            />
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="tech-card p-5">
                    <div className="overline mb-1">Last 30 Days</div>
                    <div className="font-display text-xl font-bold tracking-tight mb-4">Peak Visitor Hours</div>
                    <div className="h-72" data-testid="chart-peak">
                        <ResponsiveContainer>
                            <BarChart data={peak}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip contentStyle={{
                                    background: "hsl(var(--card))",
                                    border: "1px solid hsl(var(--border))",
                                    borderRadius: 6, fontSize: 12,
                                }} />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {peak.map((_, i) => <Cell key={i} fill="#7A1F2E" />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="tech-card p-5">
                    <div className="overline mb-1">Last 14 Days</div>
                    <div className="font-display text-xl font-bold tracking-tight mb-4">Daily Visit Trend</div>
                    <div className="h-72" data-testid="chart-trend">
                        <ResponsiveContainer>
                            <LineChart data={trend}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip contentStyle={{
                                    background: "hsl(var(--card))",
                                    border: "1px solid hsl(var(--border))",
                                    borderRadius: 6, fontSize: 12,
                                }} />
                                <Line type="monotone" dataKey="count" stroke="#7A1F2E" strokeWidth={2}
                                    dot={{ fill: "#7A1F2E", r: 3 }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="tech-card overflow-hidden lg:col-span-2">
                    <div className="p-5 border-b border-border">
                        <div className="overline mb-1">All Time</div>
                        <div className="font-display text-xl font-bold tracking-tight">Frequent Visitors · Top 10</div>
                    </div>
                    <div className="divide-y divide-border">
                        {frequent.length === 0 && (
                            <div className="p-8 text-center text-muted-foreground">No visitor history yet.</div>
                        )}
                        {frequent.map((f, idx) => (
                            <div key={f.phone} className="px-5 py-4 flex items-center gap-4" data-testid={`row-frequent-${idx}`}>
                                <div className="font-display font-black text-brand text-2xl w-8">#{idx + 1}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold">{f.name} <span className="text-xs text-muted-foreground">· {f.phone}</span></div>
                                    <div className="text-xs text-muted-foreground">
                                        {f.company || "Individual"} · Last visit: {f.last_visit ? fmtIST(f.last_visit, { date: true, time: false }) : "—"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-black text-2xl tracking-tighter">{f.visit_count}</div>
                                    <div className="overline text-[9px]">Visits</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
